import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { tuyau } from "@/config/tuyau";
import type { BookingFormData } from "@/domain/booking/booking.schema";
import type { BookingFilters } from "@/domain/booking/booking.type";

export const bookingKeys = {
  all: ["booking"] as const,
  list: (filters: BookingFilters) =>
    [...bookingKeys.all, "list", filters] as const,
  detail: (bookingId: number | null) =>
    [...bookingKeys.all, "detail", bookingId] as const,
  adminBookings: () => [...bookingKeys.all, "admin-bookings"] as const,
};

export const bookingQueries = {
  list: (filters: BookingFilters) =>
    queryOptions({
      queryKey: bookingKeys.list(filters),
      queryFn: async () => {
        const response = await tuyau.bookings.index({
          query: {
            page: filters.page,
            name: filters.name || undefined,
            priceSort: filters.priceSort || undefined,
            dateSort: filters.dateSort || undefined,
            date: filters.date || undefined,
            status: filters.status || undefined,
          },
        });

        return response;
      },
      placeholderData: (prev) => prev,
    }),
  detail: (bookingId: number | null) =>
    queryOptions({
      queryKey: bookingKeys.detail(bookingId),
      queryFn: async () => {
        if (!bookingId) {
          throw new Error("Booking ID is required");
        }

        const response = await tuyau.bookings.show({
          params: { id: bookingId },
        });
        return response;
      },
      enabled: !!bookingId,
    }),

  adminBookings: () =>
    queryOptions({
      queryKey: bookingKeys.adminBookings(),
      queryFn: async () => await tuyau.bookings.getBookingsForAdmin({}),
    }),
};

export const bookingMutations = {
  createBooking: () =>
    mutationOptions({
      mutationFn: async (data: BookingFormData) => {
        const bookingDate =
          data.booking.date instanceof Date
            ? data.booking.date.toISOString().split("T")[0]
            : new Date(data.booking.date).toISOString().split("T")[0];

        return await tuyau.bookings.store({
          body: {
            ...data,
            booking: {
              ...data.booking,
              date: bookingDate,
            },
          },
        });
      },
    }),

  refundBooking: () =>
    mutationOptions({
      mutationFn: async ({ bookingId }: { bookingId: number }) =>
        await tuyau.bookings.refundBooking({ params: { bookingId } }),
    }),

  acceptBooking: () =>
    mutationOptions({
      mutationFn: async (bookingId: number) =>
        await tuyau.bookings.accept({ params: { id: bookingId } }),
    }),

  rejectBooking: () =>
    mutationOptions({
      mutationFn: async (bookingId: number) =>
        await tuyau.bookings.reject({ params: { id: bookingId } }),
    }),
};
